import Image from 'next/image';
import Link from 'next/link';
import styles from '@/styles/screens/error-page.module.css';

type AppErrorAction = {
  href?: string;
  label: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary';
};

type AppErrorViewProps = {
  actions: AppErrorAction[];
  code: string;
  description: string;
  documentTitle?: string;
  eyebrow: string;
  title: string;
};

/**
 * 사용자가 확인 가능한 오류 복구 액션을 링크 또는 버튼 형태로 렌더링합니다.
 */
function renderAction(action: AppErrorAction) {
  const className = action.variant === 'secondary' ? styles.secondaryAction : styles.primaryAction;

  if (action.href) {
    return (
      <Link key={`${action.label}:${action.href}`} href={action.href} className={className}>
        {action.label}
      </Link>
    );
  }

  return (
    <button key={action.label} type="button" onClick={action.onClick} className={className}>
      {action.label}
    </button>
  );
}

/**
 * 오류 상태를 부드럽게 설명하는 장식용 라인 일러스트를 렌더링합니다.
 */
function AppErrorIllustration({ code }: { code: string }) {
  return (
    <div className={styles.visual} aria-hidden="true">
      <div className={styles.code}>{code}</div>
      <div className={styles.cable}>
        <span className={styles.cableLine} />
        <span className={styles.cableLoop} />
        <span className={styles.cablePlug} />
      </div>
      <div className={styles.toolMark}>
        <span className={styles.toolMarkHandle} />
        <span className={styles.toolMarkHead} />
      </div>
    </div>
  );
}

/**
 * 404와 런타임 오류 화면에서 공통으로 사용하는 한국어 오류 안내 화면입니다.
 */
export function AppErrorView({ actions, code, description, documentTitle, eyebrow, title }: AppErrorViewProps) {
  return (
    <main className={styles.shell}>
      {documentTitle ? <title>{documentTitle}</title> : null}
      <section className={styles.panel} aria-labelledby="app-error-title">
        <div className={styles.brand}>
          <span className={styles.brandLogo}>
            <Image src="/images/logo/suslogo.svg" alt="SUS" width={108} height={30} priority />
          </span>
          <span className={styles.brandText}>안전 모니터링 서비스</span>
        </div>

        <div className={styles.content}>
          <AppErrorIllustration code={code} />

          <div className={styles.copy}>
            <p className={styles.eyebrow}>{eyebrow}</p>
            <h1 id="app-error-title" className={styles.title}>
              {title}
            </h1>
            <p className={styles.description}>{description}</p>
            <div className={styles.actions}>{actions.map(renderAction)}</div>
          </div>
        </div>
      </section>
    </main>
  );
}
